import React, { Suspense, useContext, useEffect, useState } from "react";
import SignInForm from "./SignIn/SignInForm";
import CreateAccount from "./CreateAccount/CreateAccount";

import Navbar from "./HomePage/header";
import HeroSection, { primeHeroDrawings } from "./HomePage/HeroSection";
import Integrations from "./HomePage/Integrations";
import Pricing from "./HomePage/Pricing";
import AccountDetails from "./HomePage/AccountDetails";
import Languages from "./HomePage/languages";
import SupportSection from "./HomePage/SupportSection";
import Footer from "./HomePage/footer";

import BackGroundPng from "../assets/home/HeroBackground.png";
import BackGroundWebp from "../assets/home/HeroBackground.webp";
import { TransitionContext } from "../Context/TransitionContext";
import "./Home.css";
import { DataContext } from "../Context/DataContext";
import { AuthContext } from "../Context/AuthContext";
import { getQueryParam, useScreenTransition } from "../Hooks/useScreenTransition";
import { useLocation } from "react-router-dom";
import { auth, firestore } from "../firebase.config";
import { AccountType } from "../utility/enums";
import { NotificationContext } from "../Context/NotificationContext";
import { announceJoin, joinHeld, joinResultPath, joinTeamWithLink } from "../utility/teamInviteLinks";
import { goToLanding, homePathFor, returnPathFrom } from "../utility/landing";
import ForgotPasswordForm from "./SignIn/ForgotPasswordForm";
import { primeLazy } from "../utility/lazyPage";

const UniqueFeatures = React.lazy(() => import("./HomePage/UniqueFeatures"))
const Services = React.lazy(() => import("./HomePage/services"));
const UseCases = React.lazy(() => import("./HomePage/UseCases"));
const CustomerReviews = React.lazy(() => import("./HomePage/CustomerReviews"));

// The home page arrives prerendered (tools/prerender.js), and React replaces
// that markup with its own first render. index.js waits for this before that
// first render, so React draws the whole page at once, exactly as it already
// is on screen, instead of drawing it with these sections and drawings still
// missing and adding them a moment later (a visible flicker that also moved
// the page).
export const primeHome = () =>
  Promise.all([primeLazy(UniqueFeatures), primeLazy(Services), primeLazy(UseCases), primeLazy(CustomerReviews), primeHeroDrawings()]);

const menuItem = [
  {
    title: "Features",
    path: "/",
    id: "features",
  },
  {
    title: "Pricing",
    path: "/",
    id: "pricing",
  },
  {
    title: "Reviews",
    path: "/reviews",
    id: "reviews",
  },
  {
    title: "Contact",
    path: "/contact",
  },
  {
    title: "FAQs",
    path: "/faq",
  },
  {
    // Static pages served by Hosting, not React routes: needs a full page load.
    title: "Blog",
    path: "/blog",
    external: true,
  },
];

const Home = ({ windowSize }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateAccountOpen, setIsCreateAccountOpen] = useState(false);

  const { transitionTo } = useScreenTransition();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  const location = useLocation();
  const forgotPass= getQueryParam("forgot-password");
  const createAccount= getQueryParam("create-account");
  // /?sign-in=true opens the sign-in pop-up (the emails' "Sign in" buttons).
  // Already signed in, the page sends the person on to their orders.
  const signInLink= getQueryParam("sign-in");
  const redirect= getQueryParam("redirect");
  const joinTeam = getQueryParam("join-team");
  const joinTeamToken = getQueryParam("token");
  const id = location.hash ? location.hash.substring(1) : null;

  const { signedIn } = useContext(AuthContext);

  const { userData, teamData } = useContext(DataContext);
  const { showNotification } = useContext(NotificationContext);

  // WebP (13 KB) instead of the 600 KB PNG, which was the largest paint of the
  // page; browsers without WebP (TransitionContext.webP === "png") keep the PNG.
  const { webP } = useContext(TransitionContext);
  const BackGround = webP === "png" ? BackGroundPng : BackGroundWebp;

  useEffect(() => {
    if(userData && signedIn){
      if(userData.teamId){
        if(!teamData){
          return;
        }
        // A new team's owner or admin sets the team up first.
        if(teamData.newTeam && (userData.teamRole === AccountType.OWNER || userData.teamRole === AccountType.ADMIN) ){
          transitionTo("/account/onboarding")
          return;
        }
      }
      if(redirect === "false" || id){
        return;
      }
      // Signed in, the home page stays (13-oct-2026: typing everbary.com sent
      // clients to their orders, so they couldn't see it; the header says
      // they're signed in). Only someone sent here from a private page goes
      // back to it, and the emails' "Sign in" link (/?sign-in=true) goes on
      // to their orders or the portal (see utility/landing).
      const back = returnPathFrom(location);
      if(back || signInLink){
        goToLanding(transitionTo, back || homePathFor(userData));
      }
    }else{
      handleShowForgotPassword();
      handleShowCreateAccount();
      if(!signedIn && signInLink && !forgotPass && !createAccount){
        setIsModalOpen(true);
      }
    }
  }, [userData, signedIn, teamData, forgotPass, createAccount, signInLink])

  const handleShowForgotPassword = () => {
    if(!signedIn && forgotPass){
      setShowForgotPassword(true);
      setIsModalOpen(true);
    }
  }

  const handleShowCreateAccount = () => {
    if(!signedIn && createAccount){
      setIsCreateAccountOpen(true);
      setIsModalOpen(false);
    }
  }

  // Signed in and arriving from a team invite link: the server checks the
  // link and adds the person to the team. Not while the create-account form
  // is making the account: it joins the team itself once the new profile is
  // saved (see joinHeld).
  useEffect(() => {
    if(joinTeam && joinTeamToken && signedIn && userData && !joinHeld(joinTeamToken)){
      joinTeamWithLink(joinTeamToken)
        .then((status) => {
          announceJoin(status, showNotification);
          goToLanding(transitionTo, joinResultPath(status) || homePathFor(userData));
        })
        .catch(() => {
          showNotification("Error", "We couldn't add you to the team. Please, try again or ask your team admin for a new link.", "error");
        });
    }
  }, [joinTeam, joinTeamToken, signedIn, userData])

  const handleLoginOpen = (newState) => {
    setIsModalOpen(newState);
  };

  const handleCreateAccountOpen = (newState) => {
    setIsCreateAccountOpen(newState);
  };

 

  // overflow-x-clip: react-slick's (transparent) arrows stick out 1 px past
  // the right edge on phones once the carousels start, which widened the page
  // to 413 px. Nothing past the edge is visible anyway; the page now keeps
  // its width, and its largest paint is not measured again.
  return (
    <div className=" bg-[#EFF3F5] md:p-4 p-2 overflow-x-clip">

      <SignInForm
        open={isModalOpen}
        isLoginFormOpen={handleLoginOpen}
        isCreateAccountOpen={handleCreateAccountOpen}
        showDirectForgotPassword={showForgotPassword}
      />
      <CreateAccount
        open={isCreateAccountOpen}
        isCreateAccountOpen={handleCreateAccountOpen}
        isLoginFormOpen={handleLoginOpen}
      />
      <div
        className="newHeroSection rounded-4xl overflow-hidden max-md:rounded-xl"
        style={{
          backgroundColor: "#0d0d2b",
          background: "radial-gradient(circle at top center, rgba(108, 81, 205, 0.3) 0%, transparent 40%), linear-gradient(to bottom, #0d0d2b, #0f2c3f)",
          backgroundBlendMode: "screen",
          backgroundImage: `url(${BackGround})`,
          backgroundSize: "cover",
        }}
      >
        <div className="pt-8 mx-4">
          <Navbar windowSize={windowSize} isLoginFormOpen={handleLoginOpen} menuItems={menuItem} />
        </div>
        <HeroSection />
      </div>
      <Suspense fallback={<div></div>}>
        <Services windowSize={windowSize} />
      </Suspense>
      <Suspense fallback={<div></div>}>
        <UniqueFeatures />
      </Suspense>
      <Integrations />
      <Pricing />
      <Suspense fallback={<div></div>}>
        <UseCases windowSize={windowSize} />
      </Suspense>
      <AccountDetails />
      <Languages />
      <Suspense fallback={<div></div>}>
        <CustomerReviews />
      </Suspense>
      <SupportSection windowSize={windowSize} />
      <Footer windowSize={windowSize} />
    </div>
  );
};

export default Home;

// import React, { useContext, useEffect, useState } from "react";
// import "firebase/compat/firestore";

// import { TransitionContext } from "../Context/TransitionContext";
// import TopBar from "../Components/TopBar";
// import SignInForm from "./SignIn/SignInForm";
// import CreateAccount from "./CreateAccount/CreateAccount";
// import NewNavbar from "./LandingPage/NewNavbar";
// import HeroSection from "./LandingPage/HeroSection";
// import TranslateUsSection from "./LandingPage/TranslateUsSection";
// import TranslationServiceSection from "./LandingPage/TranslationServiceSection";
// import UseCasesSection from "./LandingPage/UseCasesSection";
// import PopularDocumentSection from "./LandingPage/PopularDocumentSection";
// import NewFooter from "./LandingPage/NewFooter";

// import "./Home.css";

// const HomeScreen = ({ windowSize }) => {
//   const { setColor } = useContext(TransitionContext);

//   useEffect(() => {
//     setColor(false);
//   }, [setColor]);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isCreateAccountOpen, setIsCreateAccountOpen] = useState(false);

//   const handleLoginOpen = (newState) => {
//     setIsModalOpen(newState);
//   };

//   const handleCreateAccountOpen = (newState) => {
//     setIsCreateAccountOpen(newState);
//   };

//   return (
//     <>
//       <SignInForm
//         open={isModalOpen}
//         isLoginFormOpen={handleLoginOpen}
//         isCreateAccountOpen={handleCreateAccountOpen}
//       />
//       <CreateAccount
//         open={isCreateAccountOpen}
//         isCreateAccountOpen={handleCreateAccountOpen}
//         isLoginFormOpen={handleLoginOpen}
//       />

//       <TopBar />
//       <div className="homeContainer container mx-auto">
//         <NewNavbar
//           windowSize={windowSize}
//           isLoginFormOpen={handleLoginOpen}
//           isCreateAccountOpen={handleCreateAccountOpen}
//         />
//         {/* <Navbar
//           windowSize={windowSize}
//           isLoginFormOpen={handleLoginOpen}
//           isCreateAccountOpen={handleCreateAccountOpen}
//         /> */}
//         <div className="mt-20" />
//         <HeroSection />
//         <div className="xl:mt-40 mt-28" />
//         <TranslateUsSection />
//         <div className="xl:mt-80 md:mt-40 mt-28" />
//         <TranslationServiceSection />
//         <div className="xl:mt-80 md:mt-40 mt-28" />
//         <UseCasesSection />
//         <div className="xl:mt-60 md:mt-40 mt-28" />
//         <PopularDocumentSection windowSize={windowSize} />
//         <div className="xl:mt-80 md:mt-40 mt-28" />
//       </div>
//       <NewFooter />
//       {/* <div className="homeContainer">
//       { webP ? <img src={require(`../assets/${webP === 'webp' ? 'headerBg.webp' : 'headerBg.png'}`)} alt="Decorative Heading Background" className="homeHeaderBg" /> : null}

//       <Navbar
//         windowSize={windowSize}
//         isLoginFormOpen={handleLoginOpen}
//         isCreateAccountOpen={handleCreateAccountOpen}
//       />
//       <div className="headerContainer">
//         <div className="headerInfoContainer">
//           <h1 className="heading">Online Translation Services</h1>

//           <h2 className="subheading">Get your documents translated in 24 hours for just $24.95 per page.</h2>
//           <AltAnimatedButton text="Order Now" onClick={(e) => transitionTo(`${signedIn && firebase.auth().currentUser ? '/order/documents' : '/order/contact'}`,e)} fontSize={windowSize.width > 1024 ? 1.3 : windowSize.width > 767 ? (windowSize.orientation === 'portrait' ? 2.4 : 1.6) : (windowSize.orientation === 'portrait' ? 6 : 2.2)} />
//           <div className="extraInfoContainer">
//             <div className="extraInfoTextContainer" onClick={(e) => transitionTo('/business',e)}>
//               <p className="extraInfoText">Are you a business? See benefits.</p>
//               <ChevronRight className="extraInfoButton" />
//             </div>
//             <div className="extraInfoUnderline" />
//           </div>
//         </div>
//         { webP ? <img src={require(`../assets/${webP === 'webp' ? 'home.webp' : 'home.png'}`)} alt="People sharing a document" className="headerImg" /> : null}
//       </div>
//       <div className="certificationContainer">
//         <div className="certificationItemContainer">
//           <img src={require('../assets/ata-logo.png')} alt="Logo ATA" className="certificationLogo" />
//           <p className="certificationText">ATA Corporate Member #273062</p>
//         </div>
//         <div className="certificationItemContainer">
//           <img src={require('../assets/icon-3.png')} alt="Logo ATA" className="certificationLogo" />
//           <p className="certificationText">100% guaranteed acceptance by USCIS</p>
//         </div>
//         <div className="certificationItemContainer">
//           <img src={require('../assets/icon-1.png')} alt="Logo ATA" className="certificationLogo" />
//           <p className="certificationText">Trusted by 1,250+ law firms</p>
//         </div>
//         <div className="certificationItemContainer">
//           <img src={require('../assets/icon-2.png')} alt="Logo ATA" className="certificationLogo" />
//           <p className="certificationText">Same-day delivery available</p>
//         </div>
//       </div>
//       <ServicesDisplay windowSize={windowSize} />
//       <UseCasesDisplay />
//       <DocumentsDisplay />
//       <LanguagesDisplay />
//       <Footer windowSize={windowSize} />
//     </div> */}
//     </>
//   );
// };

// export default HomeScreen;
